'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  TRACE_RESULT_KEY,
  type TraceGroupCount,
  type TracePreviewDto,
} from '@terra/shared-contracts-trace';
import { SignalStatus } from '@terra/shared-schemas-orchestrator';
import { useTraceAgentLabel } from '@terra/ui-design-system';
import {
  isTraceStatusFilter,
  type TraceListGrouping,
} from '../../../store/tab/tracesTabScopedFilterStore';
import { useListPageGroupCollapse } from '../../common/useListPageGroupCollapse';
import {
  buildExecutionsGridRows,
  isExecutionsGroupHeaderRow,
  isTraceResultGroupKey,
  resolveExecutionsLoadMoreRowIndex,
} from './executions-grouping.model';

export function useExecutionsTableRows({
  traces,
  grouping,
  groups,
  hasServerGroups,
  hasMore,
  resetKey,
}: {
  traces: readonly TracePreviewDto[];
  grouping: TraceListGrouping;
  groups: readonly TraceGroupCount[];
  hasServerGroups: boolean;
  hasMore: boolean;
  resetKey: string;
}) {
  const tExecutions = useTranslations('designSystem.executions');
  const tSignalStatus = useTranslations(
    'designSystem.signals.lifecycle.status',
  );
  const tCommon = useTranslations('designSystem.common');
  const tGroupBy = useTranslations('findingsTab.groupBy');
  const getAgentLabel = useTraceAgentLabel();
  const {
    collapsedGroupKeys,
    toggleGroup,
    expandAllGroups,
    collapseGroups,
    areAllCollapsed,
  } = useListPageGroupCollapse({
    resetKey: `${grouping?.field ?? ''}:${resetKey}`,
  });
  const groupField = grouping?.field;
  const getGroupLabel = useCallback(
    (groupKey: string): string => {
      if (groupKey === '') {
        return tGroupBy('emptyValue');
      }

      switch (groupField) {
        case 'status':
          return isTraceStatusFilter(groupKey)
            ? tExecutions(`status.${groupKey}`)
            : groupKey;
        case 'agent':
          return getAgentLabel(groupKey) ?? groupKey;
        case 'result': {
          if (!isTraceResultGroupKey(groupKey)) {
            return groupKey;
          }

          switch (groupKey) {
            case TRACE_RESULT_KEY.NONE:
              return tGroupBy('emptyValue');
            case TRACE_RESULT_KEY.CONFIRMED_VULNERABILITY_OPEN:
              return `${tSignalStatus(SignalStatus.CONFIRMED_VULNERABILITY)} (${tCommon('open')})`;
            case TRACE_RESULT_KEY.CONFIRMED_VULNERABILITY_CLOSED:
              return `${tSignalStatus(SignalStatus.CONFIRMED_VULNERABILITY)} (${tCommon('closed')})`;
            default:
              return tSignalStatus(groupKey);
          }
        }

        case undefined:
          return groupKey;
      }
    },
    [getAgentLabel, groupField, tCommon, tExecutions, tGroupBy, tSignalStatus],
  );
  const rows = useMemo(
    () =>
      buildExecutionsGridRows({
        traces,
        grouping,
        groups,
        collapsedGroupKeys,
        getGroupLabel,
      }),
    [collapsedGroupKeys, getGroupLabel, grouping, groups, traces],
  );
  const groupKeys = useMemo(
    () =>
      rows.flatMap((row) =>
        isExecutionsGroupHeaderRow(row) ? [row.groupKey] : [],
      ),
    [rows],
  );

  return {
    rows,
    loadMoreRowIndex: resolveExecutionsLoadMoreRowIndex({
      rows,
      grouping,
      collapsedGroupKeys,
      hasServerGroups,
      hasMore,
    }),
    collapsedGroupKeys,
    toggleGroup,
    areAllGroupsCollapsed: areAllCollapsed(groupKeys),
    expandAllGroups,
    collapseAllGroups: () => collapseGroups(groupKeys),
  };
}
